import { useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api, { BACKEND_URL } from '../api/axios';
import AgentLayout from './AgentLayout';
import ActionMenu from './ActionMenu';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '—');

const waLink = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return null;
  return `https://wa.me/${digits.length === 10 ? '91' + digits : digits}`;
};

const isAgentAdded = (c) => c.agent_id != null || c.created_by_agent_id != null;


const CALL_BTN = 'inline-flex items-center justify-center gap-1 text-center text-xs font-semibold py-1.5 rounded-full border transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md active:scale-95';
// User types "example.com" (no protocol) — this adds "https://" before saving.
// Left untouched if the user already typed http:// or https://.
const normalizeWebsite = (value) => {
  const v = (value || '').trim();
  if (!v) return v;
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
};

// Maps common misspellings/variants to one canonical city name, so
// "Bangalore", "Banglore", "Bengaluru UrbanhandleSaveEdit", "Bengaluru, Karnataka" etc.
// all group under one filter option instead of appearing as separate entries.
const CITY_ALIASES = {
  bangalore: 'Bengaluru', banglore: 'Bengaluru', bengaluru: 'Bengaluru',
  mysore: 'Mysuru', mysuru: 'Mysuru',
  bombay: 'Mumbai', mumbai: 'Mumbai',
  calcutta: 'Kolkata', kolkata: 'Kolkata',
  madras: 'Chennai', chennai: 'Chennai',
  vizag: 'Visakhapatnam', visakhapatnam: 'Visakhapatnam',
  trivandrum: 'Thiruvananthapuram', thiruvananthapuram: 'Thiruvananthapuram',
  cochin: 'Kochi', kochi: 'Kochi',
  hubli: 'Hubballi', hubl: 'Hubballi', hubballi: 'Hubballi', hubbali: 'Hubballi',
  pune: 'Pune', poona: 'Pune',
  gurgaon: 'Gurugram', gurugram: 'Gurugram',
  mangalore: 'Mangaluru', bellary: 'Ballari', belgaum: 'Belagavi', maisur: 'Mysuru',
  tumkur: 'Tumakuru', vijayapur: 'Vijayapura', davangere: 'Davanagere', tirupur: 'Tiruppur',
  kalburagi: 'Kalaburagi', kalburgi: 'Kalaburagi', gulbarga: 'Kalaburagi',
};

const NOISE_WORDS = /\b(urban|rural|district|city|only|and near by|near by)\b/gi;



const KNOWN_CITIES = [
  'Bengaluru','Mumbai','Delhi','Hyderabad','Chennai','Kolkata','Pune','Ahmedabad','Jaipur','Surat',
  'Lucknow','Kanpur','Nagpur','Indore','Bhopal','Patna','Vadodara','Ghaziabad','Ludhiana','Agra',
  'Nashik','Faridabad','Meerut','Rajkot','Varanasi','Srinagar','Aurangabad','Dhanbad','Amritsar',
  'Ranchi','Coimbatore','Jabalpur','Gwalior','Vijayawada','Jodhpur','Madurai','Raipur','Kota',
  'Chandigarh','Guwahati','Mysuru','Hubballi','Mangaluru','Belagavi','Kochi','Thiruvananthapuram',
  'Visakhapatnam','Noida','Gurugram','Thane','Navi Mumbai','Dehradun','Bhubaneswar','Jamshedpur',
  'Udaipur','Ajmer','Tiruchirappalli','Salem','Warangal','Vellore','Kozhikode','Thrissur',
  'Kannur','Kollam','Kottayam','Alappuzha','Palakkad','Malappuram','Kasaragod',
  'Tumakuru','Davanagere','Ballari','Shivamogga','Udupi','Hassan','Mandya','Raichur',
  'Kalaburagi','Vijayapura','Bidar','Kolar','Chikkaballapur','Chitradurga','Chikkamagaluru',
    'Nellore','Dharwad','Rampur','Jaunpur','Bannur','Athani','Kurnool','Kadapa','Guntur',
  'Tirupati','Anantapur','Nizamabad','Karimnagar','Khammam','Secunderabad','Erode',
  'Tiruppur','Theni','Cuddalore','Kumbakonam','Nagercoil','Pudukkottai','Karaikal',
  'Kolhapur','Solapur','Bagalkot','Koppal','Mulbagal','Sakleshpur','Hosur','Namakkal',
  'Adilabad','Vizianagaram','Srikakulam','Bapatla','Mangalpur','Tadipatri','Nandyal',
  'Chennur','Gundur','Amalapuram','Nangal','Kuppam',
];

const editDistance = (a, b) => {
  const dp = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
  for (let j = 1; j <= b.length; j++) dp[0][j] = j;
  for (let i = 1; i <= a.length; i++)
    for (let j = 1; j <= b.length; j++)
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
  return dp[a.length][b.length];
};

const KNOWN_STATES = [
  'Andhra Pradesh','Assam','Bihar','Chhattisgarh','Delhi','Goa','Gujarat','Haryana',
  'Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra',
  'Odisha','Punjab','Rajasthan','Tamil Nadu','Telangana','Uttar Pradesh','Uttarakhand',
  'West Bengal','Jammu and Kashmir','Chandigarh','Puducherry',
];

// Returns the closest list item within a small edit distance, else null.
const fuzzyMatch = (key, list) => {
  let best = null, bestD = Infinity;
  for (const item of list) {
    const d = editDistance(key, item.toLowerCase());
    if (d < bestD) { bestD = d; best = item; }
  }
  return bestD <= (key.length <= 5 ? 1 : 2) ? best : null;
};

const isKnownCity = (key) =>
  !!CITY_ALIASES[key] || KNOWN_CITIES.some((c) => c.toLowerCase() === key);

const titleCase = (s) => s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

// "Bangaluru, karnatak" -> "Bengaluru, Karnataka", "Dehali" -> "Delhi"
const suggestCity = (raw) => {
  if (!raw) return '';
  const [first, ...rest] = raw.split(',');
  const cleaned = first.replace(NOISE_WORDS, '').replace(/\s+/g, ' ').trim();
  if (!cleaned) return raw.trim();
  const key = cleaned.toLowerCase();

  let city = CITY_ALIASES[key] || KNOWN_CITIES.find((c) => c.toLowerCase() === key);
  if (!city) city = fuzzyMatch(key, KNOWN_CITIES) || titleCase(cleaned);

  const fixState = (s) => {
    const k = s.toLowerCase();
    return KNOWN_STATES.find((x) => x.toLowerCase() === k) || fuzzyMatch(k, KNOWN_STATES) || titleCase(s);
  };
  const tail = rest.map((s) => s.trim()).filter(Boolean).map(fixState);

  return [city, ...tail].join(', ');
};

// True only for real problems: junk words, a likely city typo, or a state typo.
const isWrongCity = (raw) => {
  if (!raw) return false;
  const [first, ...rest] = raw.split(',');

  const firstTrim = first.replace(/\s+/g, ' ').trim().toLowerCase();
  const cityKey = firstTrim.replace(NOISE_WORDS, '').replace(/\s+/g, ' ').trim();



  if (
    cityKey &&
    !isKnownCity(cityKey) &&
    !KNOWN_STATES.some((s) => s.toLowerCase() === cityKey) &&
    fuzzyMatch(cityKey, KNOWN_CITIES)
  ) return true; // Bangaluru

  const stateKey = (rest[0] || '').trim().toLowerCase();
  if (
    stateKey &&
    !KNOWN_STATES.some((s) => s.toLowerCase() === stateKey) &&
    fuzzyMatch(stateKey, KNOWN_STATES)
  ) return true; // karanataka

  return false;
};

const hasWrongLocation = (c) => isWrongCity(c.city);

// Reduces a free-text location like "Bengaluru Urban, Karnataka" or
// "Only Bangalore" down to one canonical city name for grouping/filtering.
function canonicalCity(raw) {
  if (!raw) return '';
  const firstSegment = raw.split(',')[0] || '';
  let cleaned = firstSegment.replace(NOISE_WORDS, '').replace(/\s+/g, ' ').trim();
  if (!cleaned) return '';
  const alias = CITY_ALIASES[cleaned.toLowerCase()];
  const canonical = alias || cleaned;
  return canonical.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}
// Builds a browser-reachable URL for a Laravel `Storage::disk('public')` path
// (e.g. "resumes/abc123.pdf" -> "http://host/storage/resumes/abc123.pdf").
// Strips a trailing "/api" from BACKEND_URL since storage/ is served from
// the app root, not under the API prefix.
const resumeUrl = (path) => {
  if (!path) return null;
  const base = BACKEND_URL.replace(/\/api\/?$/, '').replace(/\/$/, '');
  return `${base}/storage/${path}`;
};

// Debounce delay (ms) before firing an AI suggestion request while typing.
const SUGGEST_DEBOUNCE_MS = 350;

// Visual treatment per pipeline stage — keeps the badge color meaningful
// instead of every stage looking identically blue.
const STAGE_STYLES = {
  new: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-100',
  screening: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-100',
  interview: 'bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-100',
  offered: 'bg-teal-50 text-teal-700 ring-1 ring-inset ring-teal-100',
  hired: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-100',
  rejected: 'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-100',
};

const COMPLETION_RANGES = {
  '1-25':   { label: '1% – 25%',   min: 1,  max: 25 },
  '26-50':  { label: '26% – 50%',  min: 26, max: 50 },
  '51-75':  { label: '51% – 75%',  min: 51, max: 75 },
  '76-100': { label: '76% – 100%', min: 76, max: 100 },
};
const stageStyle = (stage) => STAGE_STYLES[(stage || 'new').toLowerCase()] || STAGE_STYLES.new;

const initials = (name) =>
  (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('') || '?';

// Shared input/select styling so every field in both modals looks identical.
const INPUT_CLS =
  'w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 ' +
  'transition-shadow focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent';
const LABEL_CLS = 'block text-sm font-medium text-gray-700 mb-1.5';


const FILTER_INPUT_CLS =
  'w-full border border-purple-200 rounded-full px-3 py-1.5 text-xs text-gray-700 placeholder:text-gray-400 bg-white ' +
  'hover:border-purple-400 transition focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent';
const FILTER_LABEL_CLS = 'block text-[10px] font-semibold uppercase tracking-wide text-purple-600 mb-1 ml-1';

const FILTER_SELECT_CLS =
  'w-full appearance-none cursor-pointer border border-purple-200 rounded-full pl-3 pr-8 py-1.5 text-xs font-medium text-purple-700 ' +
  'bg-gradient-to-r from-purple-50 to-pink-50 hover:border-purple-400 transition ' +
  'focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent';


  const SORT_GETTERS = {
  name: (c) => (c.name || '').toLowerCase(),
  email: (c) => (c.email || '').toLowerCase(),
  phone: (c) => String(c.phone || ''),
  city: (c) => (c.city || '').toLowerCase(),
  stage: (c) => (c.stage || 'new').toLowerCase(),
  created: (c) => (c.created_at ? new Date(c.created_at).getTime() : 0),
};

function SortTh({ label, k, sort, onSort }) {
  const active = sort.key === k;
  return (
    <th className="px-5 py-3 font-medium">
      <button
        type="button"
        onClick={() => onSort(k)}
        className={`inline-flex items-center gap-1 uppercase hover:text-purple-600 transition ${active ? 'text-purple-600' : ''}`}
      >
        {label}
        <span className="text-[9px]">{active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </button>
    </th>
  );
}

// Matches master-city labels like "Bengaluru, Karnataka"; names that start with the typed text come first
const matchCities = (options, q) => {
  const s = (q || '').trim().toLowerCase();
  if (!s) return [];
  return options
    .filter((o) => o.toLowerCase().includes(s))
    .sort((a, b) => Number(b.toLowerCase().startsWith(s)) - Number(a.toLowerCase().startsWith(s)))
    .slice(0, 8);
};

function SearchableSelect({ value, onChange, options, allLabel }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? options.filter((o) => o.toLowerCase().includes(s)) : options;
  }, [options, q]);

  const pick = (v) => {
    onChange(v);
    setOpen(false);
    setQ('');
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={FILTER_SELECT_CLS + ' text-left truncate'}
      >
        {value || allLabel}
      </button>
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-xs">▼</span>
      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[200px] bg-white border border-purple-100 rounded-xl shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search..."
              className="w-full border border-purple-200 rounded-full px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            <button
              type="button"
              onClick={() => pick('')}
              className="block w-full text-left px-3 py-2 text-sm font-semibold text-purple-700 hover:bg-purple-50"
            >
              {allLabel}
            </button>
            {filtered.map((o) => (
              <button
                type="button"
                key={o}
                onClick={() => pick(o)}
                className={`block w-full text-left px-3 py-2 text-sm hover:bg-purple-50 hover:text-purple-600 ${
                  o === value ? 'bg-purple-50 text-purple-600 font-semibold' : 'text-gray-700'
                }`}
              >
                {o}
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="px-3 py-2 text-xs text-gray-400">No matches</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MultiSelect({ value, onChange, options, allLabel }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? options.filter((o) => o.toLowerCase().includes(s)) : options;
  }, [options, q]);

  const toggle = (o) =>
    onChange(value.includes(o) ? value.filter((v) => v !== o) : [...value, o]);

  const label =
    value.length === 0 ? allLabel : value.length === 1 ? value[0] : `${value.length} selected`;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={FILTER_SELECT_CLS + ' text-left truncate'}
      >
        {label}
      </button>
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-xs">▼</span>
      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[220px] bg-white border border-purple-100 rounded-xl shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search..."
              className="w-full border border-purple-200 rounded-full px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {value.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="block w-full text-left px-3 py-2 text-sm font-semibold text-pink-600 hover:bg-purple-50"
              >
                Clear selection
              </button>
            )}
            {filtered.map((o) => (
              <label
                key={o}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-purple-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={value.includes(o)}
                  onChange={() => toggle(o)}
                  className="h-4 w-4 rounded border-gray-300 accent-purple-600"
                />
                <span className="truncate">{o}</span>
              </label>
            ))}
            {filtered.length === 0 && (
              <p className="px-3 py-2 text-xs text-gray-400">No matches</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TagAutocomplete({ label, value, onChange, suggestUrl, options, max = 5, placeholder, error }) {
  const items = Array.isArray(value) ? value : [];
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const addItem = (v) => {
    const val = (v || '').trim();
    if (!val || items.includes(val) || items.length >= max) return;
    onChange([...items, val]);
    setInput('');
    setShow(false);
    setSuggestions([]);
  };
  const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx));

  const handleInputChange = (e) => {
    const v = e.target.value;
    setInput(v);
    if (options) {
      setSuggestions(items.length >= max ? [] : matchCities(options, v));
      setShow(true);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (v.trim().length < 2 || items.length >= max) {
      setSuggestions([]);
      setShow(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.post(suggestUrl, { query: v.trim() });
        setSuggestions(res.data?.suggestions || []);
        setShow(true);
      } catch (err) {
        console.error('Tag suggestion failed', err);
      } finally {
        setLoading(false);
      }
    }, SUGGEST_DEBOUNCE_MS);
  };

  return (
    <div className="mb-4 relative">
      <label className={LABEL_CLS}>{label} <span className="text-gray-400 font-normal">(up to {max})</span></label>
      {items.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          {items.map((it, i) => (
            <span key={i} className="inline-flex items-center gap-1 bg-purple-50 text-purple-700 text-xs font-medium pl-2.5 pr-1.5 py-1 rounded-full">
              {it}
              <button type="button" onClick={() => removeItem(i)} className="hover:text-red-600 leading-none">×</button>
            </span>
          ))}
        </div>
      )}
      {items.length < max && (
        <div className="relative">
          <input
            type="text"
            value={input}
            onChange={handleInputChange}
            onFocus={() => { if (suggestions.length > 0) setShow(true); }}
            onBlur={() => setTimeout(() => setShow(false), 150)}
            autoComplete="off"
            placeholder={placeholder}
            className={INPUT_CLS + ' pr-7'}
          />
          {loading && (
            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 border-2 border-gray-300 border-t-purple-600 rounded-full animate-spin" />
          )}
        </div>
      )}
      {show && suggestions.length > 0 && (
        <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto py-1">
          {suggestions.map((s, i) => (
            <li key={i} onMouseDown={() => addItem(s)} className="px-3 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600 cursor-pointer transition-colors">
              {s}
            </li>
          ))}
        </ul>
      )}
      {error && <p className="text-red-500 text-xs mt-1">{error[0]}</p>}
    </div>
  );
}

function FixLocationsModal({ candidates, onClose, onApplied }) {
  const [edits, setEdits] = useState({});
  const [busyKey, setBusyKey] = useState(null);
  const [error, setError] = useState('');

  const groups = useMemo(() => {
    const map = new Map();
    candidates.forEach((c) => {
      if (!c.city) return;
      map.set(c.city, (map.get(c.city) || 0) + 1);
    });
    return Array.from(map, ([raw, count]) => ({ raw, count, suggested: suggestCity(raw) }))
      .filter((g) => g.suggested !== g.raw)
      .sort((a, b) => b.count - a.count);
  }, [candidates]);

  const apply = async (g) => {
    const to = (edits[g.raw] ?? g.suggested).trim();
    if (!to) return;
    setBusyKey(g.raw);
    setError('');
    try {
      await api.put('/agent/candidates/bulk-city', { from: [g.raw], to });
      onApplied(g.raw, to);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not update location.');
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-purple-700 to-pink-600 px-6 py-5 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-white">Fix Locations</h3>
            <p className="text-purple-100 text-xs mt-0.5">
              {groups.length} spelling{groups.length === 1 ? '' : 's'} need review. Edit the correct value, then click Apply.
            </p>
          </div>
          <button onClick={onClose} className="text-purple-100 hover:text-white text-xl leading-none">×</button>
        </div>

        <div className="p-5 overflow-y-auto">
          {error && <p className="mb-3 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
          {groups.length === 0 && (
            <p className="text-center text-gray-400 py-10">All locations look correct 🎉</p>
          )}
          <div className="space-y-2">
            {groups.map((g) => (
              <div key={g.raw} className="flex items-center gap-3 border border-gray-100 rounded-xl px-4 py-2.5">
                <div className="w-1/3 min-w-0">
                  <p className="text-sm font-medium text-red-600 truncate" title={g.raw}>{g.raw}</p>
                  <p className="text-[11px] text-gray-400">{g.count} candidate{g.count > 1 ? 's' : ''}</p>
                </div>
                <span className="text-gray-300">→</span>
                <input
                  value={edits[g.raw] ?? g.suggested}
                  onChange={(e) => setEdits((p) => ({ ...p, [g.raw]: e.target.value }))}
                  className={INPUT_CLS + ' flex-1'}
                />
                <button
                  onClick={() => apply(g)}
                  disabled={busyKey === g.raw}
                  className="bg-gradient-to-r from-purple-600 to-pink-500 text-white text-xs font-semibold px-4 py-2 rounded-full disabled:opacity-50"
                >
                  {busyKey === g.raw ? '…' : 'Apply'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function AgentCandidates() {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [fixOpen, setFixOpen] = useState(false);
const [wrongLocOnly, setWrongLocOnly] = useState(false);

const wrongLocCount = useMemo(() => candidates.filter(hasWrongLocation).length, [candidates]);
useEffect(() => {
  if (wrongLocCount === 0 && wrongLocOnly) setWrongLocOnly(false);
}, [wrongLocCount, wrongLocOnly]);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', password: '', gender: '',
    city: '', preferred_locations: [], preferred_job_designations: [], experience_years: '',
    current_company: '', current_designation: '', expected_salary: '', expected_salary_period: 'year',
    skills: '', source: 'other', stage: 'new', notes: '',
  });

  const [noLocOnly, setNoLocOnly] = useState(false);
const noLocCount = useMemo(
  () => candidates.filter((c) => !c.city || !String(c.city).trim()).length,
  [candidates]
);

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const [search, setSearch] = useState('');
  const [viewCandidate, setViewCandidate] = useState(null);
  const [viewCandidateSkills, setViewCandidateSkills] = useState([]);
  const [resumeFile, setResumeFile] = useState(null);
  const resumeInputRef = useRef(null);
  const [editCandidate, setEditCandidate] = useState(null);
  const waLocation = useLocation();
  useEffect(() => {
    const c = waLocation.state && waLocation.state.editCandidate;
    if (c) {
      setEditCandidate({ ...c, source: c.source || 'other' });
      window.history.replaceState({ ...window.history.state, usr: null }, document.title);
    }
  }, [waLocation.state]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editErrors, setEditErrors] = useState({});
  const [editResumeFile, setEditResumeFile] = useState(null);
  const editResumeInputRef = useRef(null);

  const [masterCities, setMasterCities] = useState([]);

useEffect(() => {
  api.get('/locations/cities')
    .then((res) => setMasterCities(res.data || []))
    .catch((err) => console.warn('Could not load cities', err.response?.data || err.message));
}, []);

// "City, State"; if two cities would get the same label, the district is added
const cityOptions = useMemo(() => {
  const base = (c) => [c.name, c.state].filter(Boolean).join(', ');
  const counts = {};
  masterCities.forEach((c) => { const b = base(c); counts[b] = (counts[b] || 0) + 1; });
  return masterCities.map((c) => {
    const b = base(c);
    return counts[b] > 1 && c.district ? [c.name, c.district, c.state].filter(Boolean).join(', ') : b;
  });
}, [masterCities]);

  // Edit modal: candidate's real skills (skills table, shared with their own Profile page)
  const [editCandidateSkills, setEditCandidateSkills] = useState([]);
  const [loadingEditSkills, setLoadingEditSkills] = useState(false);
  const [editSkillInput, setEditSkillInput] = useState('');
  const [editSkillProficiency, setEditSkillProficiency] = useState('intermediate');
  const [editSkillSuggestions, setEditSkillSuggestions] = useState([]);
  const [showEditSkillSuggestions, setShowEditSkillSuggestions] = useState(false);
  const [editSkillLoading, setEditSkillLoading] = useState(false);
  const editSkillDebounceRef = useRef(null);

  // --- Filters: date range + location ---
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [locationFilter, setLocationFilter] = useState([]);
  const [noEmailOnly, setNoEmailOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [stateFilter, setStateFilter] = useState([]);
  const [sourceFilter, setSourceFilter] = useState('');
    const [profileFilter, setProfileFilter] = useState('');
    const [completionFilter, setCompletionFilter] = useState('');
  const [sort, setSort] = useState({ key: '', dir: '' });
const [showFilters, setShowFilters] = useState(false);
const [selectedIds, setSelectedIds] = useState(new Set());
const [bulkLoading, setBulkLoading] = useState(false);
const [page, setPage] = useState(1);
const [pageSize, setPageSize] = useState(25);

useEffect(() => {
  setPage(1);
}, [search, locationFilter, stateFilter, statusFilter, sourceFilter, profileFilter, completionFilter, dateFrom, dateTo, noEmailOnly, wrongLocOnly, noLocOnly, sort, pageSize]);
  const [convertOpen, setConvertOpen] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertErrors, setConvertErrors] = useState({});
  const [convertForm, setConvertForm] = useState({
    company_name: '', email: '', city: '', company_website: '',
    company_industry: '', company_gst: '', call_note: '',
  });

  // --- AI autocomplete: Current City ---
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);
  const cityDebounceRef = useRef(null);

  // --- AI autocomplete: Preferred Location ---
  const [prefLocSuggestions, setPrefLocSuggestions] = useState([]);
  const [showPrefLocSuggestions, setShowPrefLocSuggestions] = useState(false);
  const [prefLocLoading, setPrefLocLoading] = useState(false);
  const prefLocDebounceRef = useRef(null);

  // --- AI autocomplete: Current Company ---
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [showCompanySuggestions, setShowCompanySuggestions] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(false);
  const companyDebounceRef = useRef(null);

  // --- AI autocomplete: Current Designation ---
  const [designationSuggestions, setDesignationSuggestions] = useState([]);
  const [showDesignationSuggestions, setShowDesignationSuggestions] = useState(false);
  const [designationLoading, setDesignationLoading] = useState(false);
  const designationDebounceRef = useRef(null);

  // --- AI autocomplete: Skills ---
  const [skillsSuggestions, setSkillsSuggestions] = useState([]);
  const [showSkillsSuggestions, setShowSkillsSuggestions] = useState(false);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const skillsDebounceRef = useRef(null);

  const loadCandidates = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await api.get('/agent/candidates');
      setCandidates(res.data);
    } catch (err) {
      console.error('Failed to load candidates', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCandidates();
  }, [loadCandidates]);

  // Load the candidate's real skills for the read-only View modal.
  useEffect(() => {
    if (!viewCandidate?.id) { setViewCandidateSkills([]); return; }
    api.get(`/agent/candidates/${viewCandidate.id}/skills`)
      .then((res) => setViewCandidateSkills(res.data || []))
      .catch((err) => console.error('Failed to load candidate skills', err));
  }, [viewCandidate?.id]);

  // Load the candidate's real skills whenever the Edit modal opens for a candidate.
  useEffect(() => {
    if (!editCandidate?.id) { setEditCandidateSkills([]); return; }
    setLoadingEditSkills(true);
    api.get(`/agent/candidates/${editCandidate.id}/skills`)
      .then((res) => setEditCandidateSkills(res.data || []))
      .catch((err) => console.error('Failed to load candidate skills', err))
      .finally(() => setLoadingEditSkills(false));
  }, [editCandidate?.id]);

  const handleEditSkillInputChange = (e) => {
    const value = e.target.value;
    setEditSkillInput(value);
    if (editSkillDebounceRef.current) clearTimeout(editSkillDebounceRef.current);
    if (value.trim().length < 2) { setEditSkillSuggestions([]); setShowEditSkillSuggestions(false); return; }
    editSkillDebounceRef.current = setTimeout(async () => {
      setEditSkillLoading(true);
      try {
        const existing = editCandidateSkills.map((s) => s.skill_name);
        const res = await api.post('/ai/suggest-skills', {
          query: value.trim(),
          job_title: editCandidate?.current_designation?.trim() || null,
          company: editCandidate?.current_company?.trim() || null,
          existing_skills: existing,
        });
        setEditSkillSuggestions(res.data?.suggestions || []);
        setShowEditSkillSuggestions(true);
      } catch (err) {
        console.error('Skill suggestion failed', err);
      } finally {
        setEditSkillLoading(false);
      }
    }, SUGGEST_DEBOUNCE_MS);
  };

  const addEditCandidateSkill = async (skillName) => {
    const name = (skillName || editSkillInput).trim();
    if (!name || !editCandidate?.id) return;
    if (editCandidateSkills.some((s) => s.skill_name.toLowerCase() === name.toLowerCase())) return;
    try {
      const res = await api.post(`/agent/candidates/${editCandidate.id}/skills`, {
        skill_name: name,
        proficiency: editSkillProficiency,
      });
      setEditCandidateSkills((prev) => [...prev, res.data.skill]);
      setEditSkillInput('');
      setShowEditSkillSuggestions(false);
      setEditSkillSuggestions([]);
    } catch (err) {
      console.error('Failed to add skill', err);
    }
  };

  const removeEditCandidateSkill = async (skillId) => {
    if (!editCandidate?.id) return;
    const prevList = editCandidateSkills;
    setEditCandidateSkills((list) => list.filter((s) => s.id !== skillId));
    try {
      await api.delete(`/agent/candidates/${editCandidate.id}/skills/${skillId}`);
    } catch (err) {
      console.error('Failed to remove skill', err);
      setEditCandidateSkills(prevList); // roll back
    }
  };

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  // ---- Current City AI suggestions ----
const handleCityChange = (e) => {
  const value = e.target.value;
  setForm((f) => ({ ...f, city: value }));
  setCitySuggestions(matchCities(cityOptions, value));
  setShowCitySuggestions(true);
};
   
  
  const pickCitySuggestion = (value) => {
    setForm((f) => ({ ...f, city: value }));
    setShowCitySuggestions(false);
    setCitySuggestions([]);
  };

  // ---- Preferred Location AI suggestions ----
  const handlePrefLocChange = (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, preferred_location: value }));

    if (prefLocDebounceRef.current) clearTimeout(prefLocDebounceRef.current);
    if (value.trim().length < 2) {
      setPrefLocSuggestions([]);
      setShowPrefLocSuggestions(false);
      return;
    }
    prefLocDebounceRef.current = setTimeout(async () => {
      setPrefLocLoading(true);
      try {
        const res = await api.post('/ai/suggest-cities', { query: value.trim() });
        setPrefLocSuggestions(res.data?.suggestions || []);
        setShowPrefLocSuggestions(true);
      } catch (err) {
        console.error('Preferred location suggestion failed', err);
      } finally {
        setPrefLocLoading(false);
      }
    }, SUGGEST_DEBOUNCE_MS);
  };
  const pickPrefLocSuggestion = (value) => {
    setForm((f) => ({ ...f, preferred_location: value }));
    setShowPrefLocSuggestions(false);
    setPrefLocSuggestions([]);
  };

  // ---- Current Company AI suggestions ----
  const handleCompanyChange = (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, current_company: value }));

    if (companyDebounceRef.current) clearTimeout(companyDebounceRef.current);
    if (value.trim().length < 2) {
      setCompanySuggestions([]);
      setShowCompanySuggestions(false);
      return;
    }
    companyDebounceRef.current = setTimeout(async () => {
      setCompanyLoading(true);
      try {
        const res = await api.post('/ai/suggest-companies', { query: value.trim() });
        setCompanySuggestions(res.data?.suggestions || []);
        setShowCompanySuggestions(true);
      } catch (err) {
        console.error('Company suggestion failed', err);
      } finally {
        setCompanyLoading(false);
      }
    }, SUGGEST_DEBOUNCE_MS);
  };
  const pickCompanySuggestion = (value) => {
    setForm((f) => ({ ...f, current_company: value }));
    setShowCompanySuggestions(false);
    setCompanySuggestions([]);
  };

  // ---- Current Designation AI suggestions ----
  const handleDesignationChange = (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, current_designation: value }));

    if (designationDebounceRef.current) clearTimeout(designationDebounceRef.current);
    if (value.trim().length < 2) {
      setDesignationSuggestions([]);
      setShowDesignationSuggestions(false);
      return;
    }
    designationDebounceRef.current = setTimeout(async () => {
      setDesignationLoading(true);
      try {
        const res = await api.post('/ai/suggest-keywords', { query: value.trim() });
        setDesignationSuggestions(res.data?.suggestions || []);
        setShowDesignationSuggestions(true);
      } catch (err) {
        console.error('Designation suggestion failed', err);
      } finally {
        setDesignationLoading(false);
      }
    }, SUGGEST_DEBOUNCE_MS);
  };
  const pickDesignationSuggestion = (value) => {
    setForm((f) => ({ ...f, current_designation: value }));
    setShowDesignationSuggestions(false);
    setDesignationSuggestions([]);
  };

  // ---- Skills AI suggestions (typeahead on the token being typed) ----
  const getSkillsToken = (value) => {
    const parts = value.split(',');
    return parts[parts.length - 1].trim();
  };

  const handleSkillsChange = (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, skills: value }));

    if (skillsDebounceRef.current) clearTimeout(skillsDebounceRef.current);
    const token = getSkillsToken(value);
    if (token.length < 2) {
      setSkillsSuggestions([]);
      setShowSkillsSuggestions(false);
      return;
    }
    skillsDebounceRef.current = setTimeout(async () => {
      setSkillsLoading(true);
      try {
        const existing = value
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
          .slice(0, -1); // exclude the partial token currently being typed

        const res = await api.post('/ai/suggest-skills', {
          query: token,
          job_title: form.current_designation.trim() || null,
          company: form.current_company.trim() || null,
          existing_skills: existing,
        });
        setSkillsSuggestions(res.data?.suggestions || []);
        setShowSkillsSuggestions(true);
      } catch (err) {
        console.error('Skills suggestion failed', err);
      } finally {
        setSkillsLoading(false);
      }
    }, SUGGEST_DEBOUNCE_MS);
  };

  const pickSkillsSuggestion = (value) => {
    setForm((f) => {
      const parts = f.skills.split(',');
      parts[parts.length - 1] = ` ${value}`;
      return { ...f, skills: parts.join(',') + ', ' };
    });
    setShowSkillsSuggestions(false);
    setSkillsSuggestions([]);
  };

    // ---- Edit modal: Current City AI suggestions ----
  const handleEditCityChange = (e) => {
    const value = e.target.value;
    setEditCandidate((c) => ({ ...c, city: value }));
    setCitySuggestions(matchCities(cityOptions, value));
    setShowCitySuggestions(true);
  };
  const pickEditCitySuggestion = (value) => {
    setEditCandidate((c) => ({ ...c, city: value }));
    setShowCitySuggestions(false); setCitySuggestions([]);
  };

  // ---- Edit modal: Preferred Location AI suggestions ----
  const handleEditPrefLocChange = (e) => {
    const value = e.target.value;
    setEditCandidate((c) => ({ ...c, preferred_location: value }));
    if (prefLocDebounceRef.current) clearTimeout(prefLocDebounceRef.current);
    if (value.trim().length < 2) { setPrefLocSuggestions([]); setShowPrefLocSuggestions(false); return; }
    prefLocDebounceRef.current = setTimeout(async () => {
      setPrefLocLoading(true);
      try {
        const res = await api.post('/ai/suggest-cities', { query: value.trim() });
        setPrefLocSuggestions(res.data?.suggestions || []);
        setShowPrefLocSuggestions(true);
      } catch (err) { console.error('Preferred location suggestion failed', err); }
      finally { setPrefLocLoading(false); }
    }, SUGGEST_DEBOUNCE_MS);
  };
  const pickEditPrefLocSuggestion = (value) => {
    setEditCandidate((c) => ({ ...c, preferred_location: value }));
    setShowPrefLocSuggestions(false); setPrefLocSuggestions([]);
  };

  // ---- Edit modal: Current Company AI suggestions ----
  const handleEditCompanyChange = (e) => {
    const value = e.target.value;
    setEditCandidate((c) => ({ ...c, current_company: value }));
    if (companyDebounceRef.current) clearTimeout(companyDebounceRef.current);
    if (value.trim().length < 2) { setCompanySuggestions([]); setShowCompanySuggestions(false); return; }
    companyDebounceRef.current = setTimeout(async () => {
      setCompanyLoading(true);
      try {
        const res = await api.post('/ai/suggest-companies', { query: value.trim() });
        setCompanySuggestions(res.data?.suggestions || []);
        setShowCompanySuggestions(true);
      } catch (err) { console.error('Company suggestion failed', err); }
      finally { setCompanyLoading(false); }
    }, SUGGEST_DEBOUNCE_MS);
  };
  const pickEditCompanySuggestion = (value) => {
    setEditCandidate((c) => ({ ...c, current_company: value }));
    setShowCompanySuggestions(false); setCompanySuggestions([]);
  };

  // ---- Edit modal: Current Designation AI suggestions ----
  const handleEditDesignationChange = (e) => {
    const value = e.target.value;
    setEditCandidate((c) => ({ ...c, current_designation: value }));
    if (designationDebounceRef.current) clearTimeout(designationDebounceRef.current);
    if (value.trim().length < 2) { setDesignationSuggestions([]); setShowDesignationSuggestions(false); return; }
    designationDebounceRef.current = setTimeout(async () => {
      setDesignationLoading(true);
      try {
        const res = await api.post('/ai/suggest-keywords', { query: value.trim() });
        setDesignationSuggestions(res.data?.suggestions || []);
        setShowDesignationSuggestions(true);
      } catch (err) { console.error('Designation suggestion failed', err); }
      finally { setDesignationLoading(false); }
    }, SUGGEST_DEBOUNCE_MS);
  };
  const pickEditDesignationSuggestion = (value) => {
    setEditCandidate((c) => ({ ...c, current_designation: value }));
    setShowDesignationSuggestions(false); setDesignationSuggestions([]);
  };

  const resetForm = () => {
    setForm({
      name: '', email: '', phone: '', password: '', gender: '',
      city: '', preferred_locations: [], preferred_job_designations: [], experience_years: '',
      current_company: '', current_designation: '', expected_salary: '', expected_salary_period: 'year',
      skills: '', source: 'other', stage: 'new', notes: '',
    });
    setErrors({});
    setSuccess(false);
    setResumeFile(null);
    if (resumeInputRef.current) resumeInputRef.current.value = '';
  };

  const handleResumeChange = (e) => {
    const f = e.target.files?.[0] || null;
    setResumeFile(f);
  };

  const removeResume = () => {
    setResumeFile(null);
    if (resumeInputRef.current) resumeInputRef.current.value = '';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const kb = bytes / 1024;
    return kb < 1024 ? `${kb.toFixed(0)} KB` : `${(kb / 1024).toFixed(1)} MB`;
  };

  const openAddForm = () => {
    resetForm();
    setShowForm(true);
  };

  const closeAddForm = () => {
    setShowForm(false);
    resetForm();
  };

  const handleSubmit = async () => {
    setSaving(true);
    setErrors({});
    setSuccess(false);
    try {
      const payload = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => payload.append(`${key}[]`, v));
        } else {
          payload.append(key, value ?? '');
        }
      });
      if (resumeFile) payload.append('resume', resumeFile);

      await api.post('/agent/candidates', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess(true);
      loadCandidates();
      // brief success flash, then close the modal
      setTimeout(() => {
        closeAddForm();
      }, 700);
    } catch (err) {
      const data = err.response?.data;
      setErrors(data?.errors || { general: [data?.message || 'Something went wrong'] });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    setEditErrors({});
    try {
      const payload = new FormData();
      payload.append('_method', 'PUT'); // Laravel method-spoofing (multipart can't send real PUT)
      const fields = {
        name: editCandidate.name,
        email: editCandidate.email,
        phone: editCandidate.phone,
gender: editCandidate.gender ?? '',
        city: editCandidate.city,
        preferred_locations: editCandidate.preferred_locations ?? [],
        preferred_job_designations: editCandidate.preferred_job_designations ?? [],
        experience_years: editCandidate.experience_years,
        current_company: editCandidate.current_company,
        current_designation: editCandidate.current_designation,
        expected_salary: editCandidate.expected_salary,
        expected_salary_period: editCandidate.expected_salary_period,
        source: editCandidate.source,
        stage: editCandidate.stage,
        notes: editCandidate.notes,
      };
      Object.entries(fields).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => payload.append(`${key}[]`, v));
        } else {
          payload.append(key, value ?? '');
        }
      });
      if (editResumeFile) payload.append('resume', editResumeFile);

      const res = await api.post(`/agent/candidates/${editCandidate.id}`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCandidates((prev) => prev.map((c) => (c.id === editCandidate.id ? { ...c, ...res.data.candidate } : c)));
      loadCandidates(); // recalculates profile_completion
      setEditCandidate(null);
      setEditResumeFile(null);
    } catch (err) {
      const data = err.response?.data;
      setEditErrors(data?.errors || { general: [data?.message || 'Could not save changes.'] });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDisableCandidate = async (c) => {
  if (!window.confirm(`Disable ${c.name}? Only an admin can re-enable them.`)) return;
  try {
    const res = await api.put(`/agent/candidates/${c.id}/disable`);
    setCandidates((prev) => prev.map((x) => (x.id === c.id ? { ...x, status: res.data.candidate.status } : x)));
  } catch (err) {
    alert(err.response?.data?.message || 'Failed to disable candidate.');
  }
};

const toggleSelect = (id) => {
  setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
};

const selectableIds = () =>
  filteredCandidates.filter((c) => (c.status || 'active') === 'active').map((c) => c.id);

const allSelected = () => {
  const ids = selectableIds();
  return ids.length > 0 && ids.every((id) => selectedIds.has(id));
};

const toggleSelectAll = () => {
  setSelectedIds(allSelected() ? new Set() : new Set(selectableIds()));
};

const handleBulkDisable = async () => {
  if (selectedIds.size === 0) return;
  if (!window.confirm(`Disable ${selectedIds.size} selected candidate(s)? Only an admin can re-enable them.`)) return;
  setBulkLoading(true);
  try {
    const res = await api.post('/agent/candidates/bulk-disable', { ids: Array.from(selectedIds) });
    const done = new Set(res.data.disabled_ids);
    setCandidates((prev) => prev.map((c) => (done.has(c.id) ? { ...c, status: 'suspended' } : c)));
    setSelectedIds(new Set());
  } catch (err) {
    alert(err.response?.data?.message || 'Failed to disable selected candidates.');
  } finally {
    setBulkLoading(false);
  }
};


  const openConvert = () => {
    setConvertForm({
      company_name: editCandidate.name || '',
      email: editCandidate.email || '',
      city: editCandidate.city || '',
      company_website: '',
      company_industry: '',
      company_gst: '',
      call_note: '',
    });
    setConvertErrors({});
    setConvertOpen(true);
  };

  const handleConvert = async (e) => {
    e.preventDefault();
    setConverting(true);
    setConvertErrors({});
    try {
      const payload = { ...convertForm, company_website: normalizeWebsite(convertForm.company_website) };
      await api.post(`/agent/candidates/${editCandidate.id}/convert-to-employer`, payload);
      setCandidates((prev) => prev.filter((c) => c.id !== editCandidate.id));
      setConvertOpen(false);
      setEditCandidate(null);
      setEditResumeFile(null);
      window.alert('Converted to employer. You can find them under Employers.');
    } catch (err) {
      const data = err.response?.data;
      setConvertErrors(data?.errors || { general: [data?.message || 'Could not convert.'] });
    } finally {
      setConverting(false);
    }
  };

  const handleEditResumeChange = (e) => {
    const f = e.target.files?.[0] || null;
    setEditResumeFile(f);
  };

  const removeEditResumeSelection = () => {
    setEditResumeFile(null);
    if (editResumeInputRef.current) editResumeInputRef.current.value = '';
  };

  const closeEditModal = () => {
    setEditCandidate(null);
    setEditResumeFile(null);
    setEditErrors({});
  };

  const handleLocationApplied = (from, to) => {
  const f = String(from).toLowerCase();
  setCandidates((prev) =>
    prev.map((c) => ((c.city || '').toLowerCase() === f ? { ...c, city: to } : c))
  );
};

const clearFilters = () => {
  setNoEmailOnly(false);
  setStatusFilter('');
  setSearch('');
  setDateFrom('');
  setDateTo('');
  setLocationFilter([]);
  setStateFilter([]);
  setSourceFilter('');
  setWrongLocOnly(false);
  setNoLocOnly(false);
    setProfileFilter('');
      setCompletionFilter('');
};

const hasActiveFilters = search || dateFrom || dateTo || locationFilter.length > 0 || noEmailOnly || statusFilter || sourceFilter || stateFilter.length > 0 || wrongLocOnly || noLocOnly || profileFilter || completionFilter;

  // Unique location options for the dropdown, built from candidates' city + preferred_location.
  const locationOptions = useMemo(() => {
    const set = new Set();
    candidates.forEach((c) => {
      const a = canonicalCity(c.city);
      if (a) set.add(a);
      (c.preferred_locations || []).forEach((loc) => {
        const b = canonicalCity(loc);
        if (b) set.add(b);
      });
    });
    return ['Not Specified', ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [candidates]);


    const salaryPeriodToggle = (period, onSelect) => (
    <div className="flex gap-2 mb-1.5">
      {['month', 'year'].map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onSelect(p)}
          className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
            period === p
             ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-transparent'
              : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
          }`}
        >
          Per {p}
        </button>
      ))}
    </div>
  );

  const candidateState = (c) =>
    (c.state || ((c.city || '').split(',')[1] || '')).trim();

  const knownStates = useMemo(
    () => Array.from(new Set(candidates.map(candidateState).filter(Boolean))).sort(),
    [candidates]
  );

  const filteredCandidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    const locs = locationFilter.map((l) => l.toLowerCase());
    const from = dateFrom ? new Date(dateFrom + 'T00:00:00') : null;
    const to = dateTo ? new Date(dateTo + 'T23:59:59') : null;

    return candidates.filter((c) => {
      if (q) {
        const matchesSearch =
          c.name?.toLowerCase().includes(q) ||
          c.email?.toLowerCase().includes(q) ||
          c.phone?.toLowerCase().includes(q) ||
          c.city?.toLowerCase().includes(q);
        if (!matchesSearch) return false;
      }

      if (noEmailOnly && c.email && String(c.email).trim() !== '') return false;
       
      if (wrongLocOnly && !hasWrongLocation(c)) return false;
      
      if (noLocOnly && c.city && String(c.city).trim()) return false;

      if (statusFilter && (c.status || 'active') !== statusFilter) return false;

            if (sourceFilter && (sourceFilter === 'agent') !== isAgentAdded(c)) return false;

                  if (profileFilter) {
        const done = Number(c.profile_completion ?? 0) >= 100;
        if (profileFilter === 'complete' ? !done : done) return false;
      }

            if (completionFilter) {
        const { min, max } = COMPLETION_RANGES[completionFilter];
        const pct = Math.round(Number(c.profile_completion ?? 0));
        if (pct < min || pct > max) return false;
      }

      if (stateFilter.length > 0 && !stateFilter.some((s) => s.toLowerCase() === candidateState(c).toLowerCase())) return false;

      if (locs.length > 0) {
        const hasNoLoc = !c.city && !(c.preferred_locations && c.preferred_locations.length > 0);
        const cityCanon = canonicalCity(c.city).toLowerCase();
        const prefCanons = (c.preferred_locations || []).map((l) => canonicalCity(l).toLowerCase());
        const matchesLocation = locs.some((l) =>
          l === 'not specified' ? hasNoLoc : cityCanon === l || prefCanons.includes(l)
        );
        if (!matchesLocation) return false;
      }

      if (from || to) {
        if (!c.created_at) return false;
        const created = new Date(c.created_at);
        if (from && created < from) return false;
        if (to && created > to) return false;
      }

      return true;
    });
  }, [candidates, search, locationFilter, dateFrom, dateTo, noEmailOnly, statusFilter, sourceFilter, profileFilter, completionFilter, stateFilter, wrongLocOnly, noLocOnly]);


  const cycleSort = (key) =>
  setSort((s) =>
    s.key !== key ? { key, dir: 'asc' } : s.dir === 'asc' ? { key, dir: 'desc' } : { key: '', dir: '' }
  );

const displayedCandidates = useMemo(() => {
  const get = SORT_GETTERS[sort.key];
  if (!get) return filteredCandidates;
  const m = sort.dir === 'asc' ? 1 : -1;
  return [...filteredCandidates].sort((a, b) => {
    const x = get(a), y = get(b);
    return typeof x === 'number' ? (x - y) * m : String(x).localeCompare(String(y)) * m;
  });
}, [filteredCandidates, sort]);

const totalPages = Math.max(1, Math.ceil(displayedCandidates.length / pageSize));
const curPage = Math.min(page, totalPages);
const pagedCandidates = displayedCandidates.slice((curPage - 1) * pageSize, curPage * pageSize);

const activeFilterCount = [
  locationFilter.length > 0, stateFilter.length > 0,
  !!statusFilter, !!sourceFilter, !!dateFrom, !!dateTo, noEmailOnly, !!profileFilter, !!completionFilter,
].filter(Boolean).length;

  const field = (name, label, type = 'text', errBag = errors) => (
    <div className="mb-4">
      <label className={LABEL_CLS}>{label}</label>
      <input
        type={type}
        name={name}
        value={form[name]}
        onChange={handleChange}
        className={INPUT_CLS}
      />
      {errBag[name] && <p className="text-red-500 text-xs mt-1">{errBag[name][0]}</p>}
    </div>
  );

  // Reusable typeahead field with an AI-suggested dropdown.
  const suggestField = ({
    name, label, value, onChange, onFocus, onBlur,
    loading, showSuggestions, suggestions, onPick, placeholder, errBag = errors,
  }) => (
    <div className="mb-4 relative">
      <label className={LABEL_CLS}>{label}</label>
      <div className="relative">
        <input
          type="text"
          name={name}
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          autoComplete="off"
          placeholder={placeholder}
          className={INPUT_CLS + ' pr-7'}
        />
        {loading && (
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 border-2 border-gray-300 border-t-purple-600 rounded-full animate-spin" />
        )}
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto py-1">
          {suggestions.map((s, i) => (
            <li
              key={i}
              onMouseDown={() => onPick(s)}
              className="px-3 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600 cursor-pointer transition-colors"
            >
              {s}
            </li>
          ))}
        </ul>
      )}
      {errBag[name] && <p className="text-red-500 text-xs mt-1">{errBag[name][0]}</p>}
    </div>
  );

  // Section heading used inside both the Add and Edit modals, so the two
  // forms read as one consistent design rather than two different UIs.
  const sectionHeading = (icon, label) => (
    <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-purple-600 mb-3 pb-2 border-b border-gray-100">
      <span aria-hidden="true">{icon}</span> {label}
    </h4>
  );

  const skillsField = (value, onChangeHandler, loadingFlag, showFlag, suggestionsList, onPick) => (
    <div className="relative">
      <label className={LABEL_CLS}>Skills</label>
      <div className="relative">
        <textarea
          value={value}
          onChange={onChangeHandler}
          onFocus={() => { if (suggestionsList.length > 0) setShowSkillsSuggestions(true); }}
          onBlur={() => setTimeout(() => setShowSkillsSuggestions(false), 150)}
          autoComplete="off"
          placeholder="Comma-separated, e.g. React, Node.js, SQL"
          className={INPUT_CLS + ' resize-none'}
          rows={2}
        />
        {loadingFlag && (
          <span className="absolute right-2.5 top-3 h-3.5 w-3.5 border-2 border-gray-300 border-t-purple-600 rounded-full animate-spin" />
        )}
      </div>
      {showFlag && suggestionsList.length > 0 && (
        <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto py-1">
          {suggestionsList.map((s, i) => (
            <li
              key={i}
              onMouseDown={() => onPick(s)}
              className="px-3 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600 cursor-pointer transition-colors"
            >
              {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <AgentLayout>
      <div className="bg-gradient-to-br from-[#FAF5FC] to-[#FDF1F9] -m-6 p-6 min-h-screen">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent tracking-tight">Candidates</h2>
        <button
          onClick={openAddForm}
          className="bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 text-white text-sm font-semibold px-5 py-2.5 rounded-full shadow-sm transition"
        >
          + Add Candidate
        </button>
      </div>
      <p className="text-gray-500 text-sm mb-6">All job seekers in the system.</p>

      {/* ---- Add Candidate Modal ---- */}
      {showForm && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-50 p-4"
          onClick={closeAddForm}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-700 to-pink-600 px-6 py-5 flex items-start justify-between shrink-0">
              <div>
                <h3 className="text-lg font-bold text-white">Add New Candidate</h3>
                <p className="text-purple-100 text-xs mt-0.5">Fill in the details to register a job seeker</p>
              </div>
              <button
                onClick={closeAddForm}
                className="text-purple-100 hover:text-white hover:bg-white/10 rounded-full h-8 w-8 flex items-center justify-center text-xl leading-none transition-colors"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 overflow-y-auto">
              {success && (
                <div className="mb-4 bg-green-50 text-green-700 text-sm px-4 py-2.5 rounded-lg border border-green-100 flex items-center gap-2">
                  <span>✓</span> Candidate created successfully.
                </div>
              )}
              {errors.general && (
                <div className="mb-4 bg-red-50 text-red-700 text-sm px-4 py-2.5 rounded-lg border border-red-100">
                  {errors.general[0]}
                </div>
              )}

              {/* Section: Personal Details */}
              <div className="mb-5">
                {sectionHeading('👤', 'Personal Details')}
                <div className="grid grid-cols-2 gap-4">
                  {field('name', 'Full Name')}
                  {field('email', 'Email')}
                  {field('phone', 'Phone (10 digit)')}
                  {field('password', 'Set Login Password', 'password')}
<div className="mb-4">
  <label className={LABEL_CLS}>Gender</label>
  <select
    name="gender"
    value={form.gender}
    onChange={handleChange}
    className={INPUT_CLS + ' bg-white'}
  >
    <option value="">Select gender</option>
    <option value="male">Male</option>
    <option value="female">Female</option>
    <option value="other">Other</option>
    <option value="prefer not to say">Prefer not to say</option>
  </select>
  {errors.gender && <p className="text-red-500 text-xs mt-1">{errors.gender[0]}</p>}
</div>
                </div>
              </div>

              {/* Section: Location & Experience */}
              <div className="mb-5">
                {sectionHeading('📍', 'Location & Experience')}
                <div className="grid grid-cols-2 gap-4">
                  {suggestField({
                    name: 'city',
                    label: 'Current City',
                    value: form.city,
                    onChange: handleCityChange,
                    onFocus: () => { if (citySuggestions.length > 0) setShowCitySuggestions(true); },
                    onBlur: () => setTimeout(() => setShowCitySuggestions(false), 150),
                    loading: cityLoading,
                    showSuggestions: showCitySuggestions,
                    suggestions: citySuggestions,
                    onPick: pickCitySuggestion,
                    placeholder: 'e.g. Bengaluru, Karnataka',
                  })}

                  {field('experience_years', 'Experience (years)', 'number')}
                  <div className="mb-4">
                    <label className={LABEL_CLS}>Expected Salary (₹)</label>
                    {salaryPeriodToggle(form.expected_salary_period, (p) => setForm((f) => ({ ...f, expected_salary_period: p })))}
                    <input
                      type="number"
                      name="expected_salary"
                      value={form.expected_salary}
                      onChange={handleChange}
                      className={INPUT_CLS}
                    />
                    {errors.expected_salary && <p className="text-red-500 text-xs mt-1">{errors.expected_salary[0]}</p>}
                  </div>
                </div>

                <TagAutocomplete
                  label="Preferred Location"
                  value={form.preferred_locations}
                  onChange={(v) => setForm((f) => ({ ...f, preferred_locations: v }))}
                  options={cityOptions}
                  placeholder="e.g. Pune, Maharashtra"
                  max={5}
                  error={errors.preferred_locations}
                />
              </div>

              {/* Section: Professional Details */}
              <div className="mb-5">
                {sectionHeading('💼', 'Professional Details')}
                <div className="grid grid-cols-2 gap-4">
                  {suggestField({
                    name: 'current_company',
                    label: 'Current Company',
                    value: form.current_company,
                    onChange: handleCompanyChange,
                    onFocus: () => { if (companySuggestions.length > 0) setShowCompanySuggestions(true); },
                    onBlur: () => setTimeout(() => setShowCompanySuggestions(false), 150),
                    loading: companyLoading,
                    showSuggestions: showCompanySuggestions,
                    suggestions: companySuggestions,
                    onPick: pickCompanySuggestion,
                  })}

                  {suggestField({
                    name: 'current_designation',
                    label: 'Current Designation',
                    value: form.current_designation,
                    onChange: handleDesignationChange,
                    onFocus: () => { if (designationSuggestions.length > 0) setShowDesignationSuggestions(true); },
                    onBlur: () => setTimeout(() => setShowDesignationSuggestions(false), 150),
                    loading: designationLoading,
                    showSuggestions: showDesignationSuggestions,
                    suggestions: designationSuggestions,
                    onPick: pickDesignationSuggestion,
                  })}
                </div>

                <TagAutocomplete
                  label="Preferred Designation"
                  value={form.preferred_job_designations}
                  onChange={(v) => setForm((f) => ({ ...f, preferred_job_designations: v }))}
                  suggestUrl="/ai/suggest-keywords"
                  placeholder="e.g. Senior Developer"
                  max={10}
                  error={errors.preferred_job_designations}
                />

                {skillsField(
                  form.skills,
                  handleSkillsChange,
                  skillsLoading,
                  showSkillsSuggestions,
                  skillsSuggestions,
                  pickSkillsSuggestion
                )}
              </div>

              {/* Section: Resume */}
              <div className="mb-5">
                {sectionHeading('📄', 'Resume')}
                {!resumeFile ? (
                  <label
                    htmlFor="resume-upload"
                    className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl py-6 cursor-pointer hover:border-purple-500 hover:bg-purple-50/40 transition-colors"
                  >
                    <span className="text-2xl mb-1">⬆️</span>
                    <span className="text-sm font-medium text-gray-600">Click to upload resume</span>
                    <span className="text-xs text-gray-400 mt-0.5">PDF, DOC or DOCX — up to 5MB</span>
                    <input
                      id="resume-upload"
                      ref={resumeInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleResumeChange}
                      className="hidden"
                    />
                  </label>
                ) : (
                  <div className="flex items-center justify-between border border-gray-200 rounded-xl px-4 py-3 bg-gray-50">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-xl">📄</span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-700 truncate">{resumeFile.name}</p>
                        <p className="text-xs text-gray-400">{formatFileSize(resumeFile.size)}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={removeResume}
                      className="text-xs font-semibold text-red-500 hover:text-red-600 shrink-0 ml-3"
                    >
                      Remove
                    </button>
                  </div>
                )}
                {errors.resume && <p className="text-red-500 text-xs mt-1">{errors.resume[0]}</p>}
              </div>

              {/* Section: Source & Notes */}
              <div>
                {sectionHeading('🗂️', 'Source & Notes')}
                <div className="grid grid-cols-2 gap-4 items-start">
                  <div className="mb-4">
                    <label className={LABEL_CLS}>Source</label>
                    <select
                      name="source"
                      value={form.source}
                      onChange={handleChange}
                      className={INPUT_CLS + ' bg-white'}
                    >
                      <option value="referral">Referral</option>
                      <option value="naukri">Naukri</option>
                      <option value="linkedin">LinkedIn</option>
                      <option value="walk_in">Walk-in</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  {field('notes', 'Notes')}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 shrink-0">
              <button
                type="button"
                onClick={closeAddForm}
                className="text-sm font-medium text-gray-500 hover:text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 text-white px-6 py-2 rounded-full text-sm font-semibold disabled:opacity-50 transition shadow-sm"
              >
                {saving ? 'Saving...' : 'Add Candidate'}
              </button>
            </div>
          </div>
        </div>
      )}

    {/* ---- Search + Filters ---- */}
<div className="bg-white rounded-2xl shadow-sm border border-purple-100 p-3 mb-4">
  <div className="flex flex-col sm:flex-row gap-3">
    <div className="relative flex-1">
      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm pointer-events-none">🔍</span>
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by name, email, phone or city..."
        className={FILTER_INPUT_CLS + ' pl-10 pr-10 py-2'}
      />
      {search && (
        <button
          type="button"
          onClick={() => setSearch('')}
          className="absolute right-3 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full text-gray-400 hover:bg-purple-50 hover:text-purple-600"
          aria-label="Clear search"
        >
          ×
        </button>
      )}
    </div>

    <button
      type="button"
      onClick={() => setShowFilters((s) => !s)}
      className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition ${
        showFilters || activeFilterCount > 0
          ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-transparent shadow-sm'
          : 'bg-white text-purple-700 border-purple-200 hover:border-purple-400'
      }`}
    >
      ⚙ Filters
      {activeFilterCount > 0 && (
        <span className="bg-white text-purple-600 text-[11px] font-bold h-5 min-w-[1.25rem] px-1 rounded-full flex items-center justify-center">
          {activeFilterCount}
        </span>
      )}
      <span className="text-[10px]">{showFilters ? '▲' : '▼'}</span>
    </button>

{wrongLocCount > 0 && (
  <>
    <button
      type="button"
      onClick={() => { setWrongLocOnly((v) => !v); setNoLocOnly(false); }}
      className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition ${
        wrongLocOnly
          ? 'bg-amber-500 text-white border-transparent shadow-sm'
          : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
      }`}
    >
      ⚠ Wrong location
      <span className="bg-white text-amber-700 text-[11px] font-bold h-5 min-w-[1.25rem] px-1 rounded-full flex items-center justify-center">
        {wrongLocCount}
      </span>
    </button>
    {/* <button
      type="button"
      onClick={() => setFixOpen(true)}
      className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-purple-600 to-pink-500 text-white shadow-sm hover:opacity-90 transition"
    >
      ✏️ Fix locations
    </button> */}
  </>
)}

{noLocCount > 0 && (
  <button
    type="button"
    onClick={() => { setNoLocOnly((v) => !v); setWrongLocOnly(false); }}
    className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition ${
      noLocOnly
        ? 'bg-rose-500 text-white border-transparent shadow-sm'
        : 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100'
    }`}
  >
    📍 No location
    <span className="bg-white text-rose-700 text-[11px] font-bold h-5 min-w-[1.25rem] px-1 rounded-full flex items-center justify-center">
      {noLocCount}
    </span>
  </button>
)}

    {hasActiveFilters && (
      <button
        onClick={clearFilters}
        className="text-sm font-semibold px-4 py-2 rounded-full bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100 transition"
      >
        ✕ Clear all
      </button>
    )}
  </div>

{showFilters && (
  <div className="mt-3 pt-3 border-t border-purple-50">
    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-9 gap-3">
        <div>
          <label className={FILTER_LABEL_CLS}>Location</label>
          <MultiSelect value={locationFilter} onChange={setLocationFilter} options={locationOptions} allLabel="All Locations" />
        </div>
        <div>
          <label className={FILTER_LABEL_CLS}>State</label>
          <MultiSelect value={stateFilter} onChange={setStateFilter} options={knownStates} allLabel="All States" />
        </div>
        <div>
          <label className={FILTER_LABEL_CLS}>Status</label>
          <div className="relative">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={FILTER_SELECT_CLS}>
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-xs">▼</span>
          </div>
        </div>

                       <div>
          <label className={FILTER_LABEL_CLS}>Source</label>
          <div className="relative">
            <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className={FILTER_SELECT_CLS}>
              <option value="">All</option>
              <option value="agent">Added by agent</option>
              <option value="direct">Direct register</option>
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-xs">▼</span>
          </div>
        </div>
               <div>
          <label className={FILTER_LABEL_CLS}>Profile</label>
          <div className="relative">
            <select value={profileFilter} onChange={(e) => setProfileFilter(e.target.value)} className={FILTER_SELECT_CLS}>
              <option value="">All</option>
              <option value="complete">Completed (100%)</option>
              <option value="incomplete">Not completed</option>
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-xs">▼</span>
          </div>
        </div>
                <div>
          <label className={FILTER_LABEL_CLS}>Completion %</label>
          <div className="relative">
            <select value={completionFilter} onChange={(e) => setCompletionFilter(e.target.value)} className={FILTER_SELECT_CLS}>
              <option value="">All</option>
              {Object.entries(COMPLETION_RANGES).map(([key, r]) => (
                <option key={key} value={key}>{r.label}</option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-xs">▼</span>
          </div>
        </div>

        <div className="flex items-end">
          <label
            className={`flex items-center gap-2 text-xs cursor-pointer select-none px-3 py-1.5 rounded-full border transition w-full ${
              noEmailOnly
                ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-transparent shadow-sm'
                : 'bg-white text-gray-600 border-purple-200 hover:border-purple-400'
            }`}
          >
            <input
              type="checkbox"
              checked={noEmailOnly}
              onChange={(e) => setNoEmailOnly(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 accent-purple-600"
            />
            No email only
          </label>
        </div>
        <div>
          <label className={FILTER_LABEL_CLS}>Added From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={FILTER_INPUT_CLS} />
        </div>
        <div>
          <label className={FILTER_LABEL_CLS}>Added To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={FILTER_INPUT_CLS} />
        </div>
      </div>
  </div>
)}

  {/* Active filter chips + count */}
  <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
    <div className="flex flex-wrap gap-2">
      {[
        ...locationFilter.map((v) => ({ k: 'l' + v, label: '📍 ' + v, remove: () => setLocationFilter(locationFilter.filter((x) => x !== v)) })),
        ...stateFilter.map((v) => ({ k: 's' + v, label: v, remove: () => setStateFilter(stateFilter.filter((x) => x !== v)) })),
        ...(statusFilter ? [{ k: 'st', label: 'Status: ' + statusFilter, remove: () => setStatusFilter('') }] : []),
        ...(sourceFilter ? [{ k: 'src', label: sourceFilter === 'agent' ? 'Added by agent' : 'Direct register', remove: () => setSourceFilter('') }] : []),
                ...(profileFilter ? [{ k: 'pf', label: profileFilter === 'complete' ? 'Profile completed' : 'Profile not completed', remove: () => setProfileFilter('') }] : []),
               ...(completionFilter ? [{ k: 'pc', label: 'Completion ' + COMPLETION_RANGES[completionFilter].label, remove: () => setCompletionFilter('') }] : []),
                ...(dateFrom ? [{ k: 'df', label: 'From ' + fmtDate(dateFrom), remove: () => setDateFrom('') }] : []),
        ...(dateTo ? [{ k: 'dt', label: 'To ' + fmtDate(dateTo), remove: () => setDateTo('') }] : []),
        ...(noEmailOnly ? [{ k: 'ne', label: 'No email', remove: () => setNoEmailOnly(false) }] : []),
        ...(wrongLocOnly ? [{ k: 'wl', label: 'Wrong location', remove: () => setWrongLocOnly(false) }] : []),
        ...(noLocOnly ? [{ k: 'nl', label: 'No location', remove: () => setNoLocOnly(false) }] : []),
      
      ].map((c) => (
        <span key={c.k} className="inline-flex items-center gap-1.5 text-xs font-medium pl-3 pr-1.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-100 capitalize">
          {c.label}
          <button type="button" onClick={c.remove} className="h-4 w-4 rounded-full hover:bg-purple-200 leading-none" aria-label="Remove filter">×</button>
        </span>
      ))}
    </div>
    <p className="text-xs text-gray-400">
      Showing <span className="font-semibold text-purple-600">{filteredCandidates.length}</span> of {candidates.length} candidates
    </p>
  </div>
</div>

      {selectedIds.size > 0 && (
  <div className="flex items-center gap-3 bg-purple-50 border border-purple-100 rounded-xl px-4 py-3 mb-3 flex-wrap">
    <span className="text-sm text-purple-700 font-medium">{selectedIds.size} selected</span>
    <button
      onClick={handleBulkDisable}
      disabled={bulkLoading}
      className="text-xs font-semibold px-4 py-1.5 rounded-full bg-gradient-to-r from-purple-600 to-pink-500 text-white shadow-sm hover:opacity-90 disabled:opacity-50"
    >
      {bulkLoading ? 'Disabling…' : 'Disable selected'}
    </button>
    <button
      onClick={() => setSelectedIds(new Set())}
      disabled={bulkLoading}
      className="text-xs font-semibold text-gray-500 hover:text-gray-700"
    >
      Clear
    </button>
  </div>
)}

      <div className="flex items-center justify-between flex-wrap gap-2 mb-2 text-sm text-gray-600">
        <span className="font-semibold">
          Showing {displayedCandidates.length === 0 ? 0 : (curPage - 1) * pageSize + 1}–{Math.min(curPage * pageSize, displayedCandidates.length)} of {displayedCandidates.length}
        </span>
        <select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          className="border border-purple-200 rounded-full px-2 py-1 text-xs bg-white"
        >
          {[10, 25, 50, 100].map((n) => (
            <option key={n} value={n}>{n} / page</option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm table-auto">
          <thead>
           <tr className="text-left text-gray-400 text-[10px] uppercase border-b border-gray-100 bg-gray-50/60">
  <th className="px-5 py-3 w-8">
    <input type="checkbox" checked={allSelected()} onChange={toggleSelectAll} />
  </th>
  <SortTh label="Name" k="name" sort={sort} onSort={cycleSort} />
  <SortTh label="Email" k="email" sort={sort} onSort={cycleSort} />
  <SortTh label="Phone" k="phone" sort={sort} onSort={cycleSort} />
  <SortTh label="City" k="city" sort={sort} onSort={cycleSort} />
  <SortTh label="Stage" k="stage" sort={sort} onSort={cycleSort} />
  <SortTh label="Added" k="created" sort={sort} onSort={cycleSort} />
 <th className="px-5 py-3 font-medium w-[140px] min-w-[140px] sticky right-0 bg-gray-50 z-10">Action</th>
</tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="py-10 text-center text-gray-400">
                  <span className="inline-block h-4 w-4 border-2 border-gray-300 border-t-purple-600 rounded-full animate-spin align-middle mr-2" />
                  Loading…
                </td>
              </tr>
            )}
            {!loading && loadError && (
              <tr>
                <td colSpan={8} className="py-10 text-center text-red-500">
                  Could not load candidates.{' '}
                  <button onClick={loadCandidates} className="underline font-medium hover:text-red-600">
                    Retry
                  </button>
                </td>
              </tr>
            )}
            {!loading && !loadError && filteredCandidates.length === 0 && (
              <tr>
                <td colSpan={8} className="py-10 text-center text-gray-400">
                  {hasActiveFilters ? 'No candidates match your filters.' : 'No candidates yet.'}
                </td>
              </tr>
            )}
            {!loading && !loadError && pagedCandidates.map((c) => (
              <tr key={c.id} className="border-t border-gray-50 hover:bg-purple-50/30 transition-colors">

                <td className="px-5 py-3">
  <input
    type="checkbox"
    checked={selectedIds.has(c.id)}
    disabled={(c.status || 'active') !== 'active'}
    onChange={() => toggleSelect(c.id)}
  />
</td>

                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <span className="h-8 w-8 shrink-0 rounded-full bg-purple-50 text-purple-600 text-xs font-semibold flex items-center justify-center">
                      {initials(c.name)}
                    </span>
                    <button
  type="button"
  onClick={() => setViewCandidate(c)}
  className="font-medium text-left text-[#7C3AED] hover:underline"
>
  {isAgentAdded(c) && (
    <span title="Added by agent" className="mr-1 font-bold text-pink-600">(A)</span>
  )}
  {c.name}
</button>
                  </div>
                </td>
                <td className="px-5 py-3 text-gray-600">{c.email}</td>
                <td className="px-5 py-3 text-gray-600">{c.phone || '—'}</td>
                <td className="px-5 py-3 text-gray-600">
  {hasWrongLocation(c) ? (
    <span className="text-red-600" title={`Suggested: ${suggestCity(c.city)}`}>{c.city}</span>
  ) : (c.city || '—')}
</td>
                <td className="px-5 py-3">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full capitalize ${stageStyle(c.stage)}`}>
                    {c.stage || 'new'}
                  </span>
                  <span className="ml-2 text-[10px] font-semibold text-gray-400">{c.profile_completion ?? 0}%</span>
                </td>
                <td className="px-5 py-3 text-gray-500">{fmtDate(c.created_at)}</td>
              <td className="px-5 py-3 w-[140px] min-w-[140px] sticky right-0 bg-white z-10 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.12)]">
  <ActionMenu
    phone={c.phone}
    items={[
      { label: 'View', icon: '👁', tone: 'purple', onClick: () => setViewCandidate(c) },
      { label: 'Edit', icon: '✏️', tone: 'blue', onClick: () => setEditCandidate({ ...c, source: c.source || 'other' }) },
      (c.status || 'active') === 'active'
        ? { label: 'Disable', icon: '🚫', tone: 'amber', onClick: () => handleDisableCandidate(c) }
        : { label: `Status: ${c.status}`, icon: '⛔', tone: 'off', disabled: true },
    ]}
  />
</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

            {(() => {
        const total = displayedCandidates.length;
        const from = total === 0 ? 0 : (curPage - 1) * pageSize + 1;
        const to = Math.min(curPage * pageSize, total);
        const nums = [];
        for (let p = Math.max(1, curPage - 2); p <= Math.min(totalPages, curPage + 2); p++) nums.push(p);
        const btn = 'px-3 py-1.5 rounded-full text-xs font-semibold border disabled:opacity-40';
        return (
          <div className="flex items-center justify-between flex-wrap gap-3 mt-3 text-sm text-gray-600">
            <div className="flex items-center gap-3">
              <span>Showing {from}–{to} of {total}</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="border border-purple-200 rounded-full px-2 py-1 text-xs bg-white"
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>{n} / page</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-1">
              <button className={btn} disabled={curPage === 1} onClick={() => setPage(1)}>«</button>
              <button className={btn} disabled={curPage === 1} onClick={() => setPage(curPage - 1)}>Prev</button>
              {nums.map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`${btn} ${p === curPage ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-transparent' : 'bg-white border-purple-200'}`}
                >
                  {p}
                </button>
              ))}
              <button className={btn} disabled={curPage === totalPages} onClick={() => setPage(curPage + 1)}>Next</button>
              <button className={btn} disabled={curPage === totalPages} onClick={() => setPage(totalPages)}>»</button>
            </div>
          </div>
        );
      })()}

      {/* ---- View Candidate Modal ---- */}
      {viewCandidate && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-50 p-4"
          onClick={() => setViewCandidate(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-purple-700 to-pink-600 px-6 py-5 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <span className="h-10 w-10 rounded-full bg-white/10 text-white text-sm font-semibold flex items-center justify-center">
                  {initials(viewCandidate.name)}
                </span>
                <div>
                  <h3 className="text-lg font-bold text-white leading-tight">{viewCandidate.name}</h3>
                  <span className={`inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full capitalize ${stageStyle(viewCandidate.stage)}`}>
                    {viewCandidate.stage || 'new'}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setViewCandidate(null)}
                className="text-purple-100 hover:text-white hover:bg-white/10 rounded-full h-8 w-8 flex items-center justify-center text-xl leading-none transition-colors"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <dl className="px-6 py-5 overflow-y-auto text-sm divide-y divide-gray-50">
              {[
                ['Email', viewCandidate.email],
                ['Phone', viewCandidate.phone || '—'],
                ['City', viewCandidate.city || '—'],
                ['Preferred Location', (viewCandidate.preferred_locations || []).join(', ') || '—'],
                ['Preferred Designation', (viewCandidate.preferred_job_designations || []).join(', ') || '—'],
                ['Experience', `${viewCandidate.experience_years ?? '—'} yrs`],
                ['Current Company', viewCandidate.current_company || '—'],
                ['Current Designation', viewCandidate.current_designation || '—'],
                ['Expected Salary', viewCandidate.expected_salary ? `₹${viewCandidate.expected_salary} / ${viewCandidate.expected_salary_period || 'year'}` : '—'],
                ['Skills', viewCandidateSkills.length > 0 ? viewCandidateSkills.map((s) => s.skill_name).join(', ') : '—'],
              ].map(([label, value]) => (
                <div key={label} className="flex items-start justify-between gap-4 py-2">
                  <dt className="text-gray-400 shrink-0">{label}</dt>
                  <dd className="text-gray-700 text-right">{value}</dd>
                </div>
              ))}
              <div className="flex items-start justify-between gap-4 py-2">
                <dt className="text-gray-400 shrink-0">Resume</dt>
                <dd className="text-right">
                  {viewCandidate.resume_path ? (
                    <a
                      href={resumeUrl(viewCandidate.resume_path)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-600 font-medium hover:underline"
                    >
                      View resume →
                    </a>
                  ) : '—'}
                </dd>
              </div>
              <div className="flex items-start justify-between gap-4 py-2">
                <dt className="text-gray-400 shrink-0">Source</dt>
                <dd className="text-gray-700 capitalize text-right">{(viewCandidate.source || '').replace('_', ' ') || '—'}</dd>
              </div>
              <div className="flex items-start justify-between gap-4 py-2">
                <dt className="text-gray-400 shrink-0">Notes</dt>
                <dd className="text-gray-700 text-right">{viewCandidate.notes || '—'}</dd>
              </div>
              <div className="flex items-start justify-between gap-4 py-2">
                <dt className="text-gray-400 shrink-0">Added</dt>
                <dd className="text-gray-700 text-right">{fmtDate(viewCandidate.created_at)}</dd>
              </div>
            </dl>
            <div className="flex items-center gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50 shrink-0">
  {viewCandidate.phone ? (
    <a href={`tel:${viewCandidate.phone}`} className={`${CALL_BTN} flex-1 py-2 bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100`}>📞 Call</a>
  ) : (
    <span className={`${CALL_BTN} flex-1 py-2 bg-gray-50 text-gray-300 border-gray-200`}>📞 Call</span>
  )}
  {waLink(viewCandidate.phone) ? (
    <a href={waLink(viewCandidate.phone)} target="_blank" rel="noreferrer" className={`${CALL_BTN} flex-1 py-2 bg-green-50 text-green-600 border-green-200 hover:bg-green-100`}>💬 WhatsApp</a>
  ) : (
    <span className={`${CALL_BTN} flex-1 py-2 bg-gray-50 text-gray-300 border-gray-200`}>💬 WhatsApp</span>
  )}
</div>
          </div>
        </div>
      )}

      {/* ---- Edit Candidate Modal ---- */}
      {editCandidate && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-50 p-4"
          onClick={closeEditModal}
        >
          <form
            onSubmit={handleSaveEdit}
            className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header — mirrors the Add Candidate modal so both feel like one design */}
            <div className="bg-gradient-to-r from-purple-700 to-pink-600 px-6 py-5 flex items-start justify-between shrink-0">
              <div>
                <h3 className="text-lg font-bold text-white">Edit Candidate</h3>
                <p className="text-purple-100 text-xs mt-0.5">Update {editCandidate.name || 'this candidate'}&rsquo;s details</p>
              </div>
              <button
                type="button"
                onClick={closeEditModal}
                className="text-purple-100 hover:text-white hover:bg-white/10 rounded-full h-8 w-8 flex items-center justify-center text-xl leading-none transition-colors"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="px-6 py-5 overflow-y-auto">
              {editErrors.general && (
                <div className="mb-4 bg-red-50 text-red-700 text-sm px-4 py-2.5 rounded-lg border border-red-100">
                  {editErrors.general[0]}
                </div>
              )}

              {/* Section: Personal Details */}
              <div className="mb-5">
                {sectionHeading('👤', 'Personal Details')}
                <div className="grid grid-cols-2 gap-4">
                  {[
                    ['name', 'Full Name', 'text'],
                    ['email', 'Email', 'email'],
                    ['phone', 'Phone (10 digit)', 'text'],
                  ].map(([name, label, type]) => (
                    <div key={name}>
                      <label className={LABEL_CLS}>{label}</label>
                      <input
                        type={type}
                        value={editCandidate[name] ?? ''}
                        onChange={(e) => setEditCandidate((c) => ({ ...c, [name]: e.target.value }))}
                        className={INPUT_CLS}
                      />
                      {editErrors[name] && <p className="text-red-500 text-xs mt-1">{editErrors[name][0]}</p>}
                    </div>
                  ))}
                  <div>
                    <label className={LABEL_CLS}>Gender</label>
                    <select
                      value={(editCandidate.gender ?? '').toLowerCase()}
                      onChange={(e) => setEditCandidate((c) => ({ ...c, gender: e.target.value }))}
                      className={INPUT_CLS + ' bg-white'}
                    >
                      <option value="">Select gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                      <option value="prefer not to say">Prefer not to say</option>
                    </select>
                    {editErrors.gender && <p className="text-red-500 text-xs mt-1">{editErrors.gender[0]}</p>}
                  </div>
                </div>
              </div>

                          {/* Section: Location & Experience */}
              <div className="mb-5">
                {sectionHeading('📍', 'Location & Experience')}
                <div className="grid grid-cols-2 gap-4 items-start">
                  {suggestField({
                    name: 'city', label: 'Current City', value: editCandidate.city ?? '',
                    onChange: handleEditCityChange,
                    onFocus: () => { if (citySuggestions.length > 0) setShowCitySuggestions(true); },
                    onBlur: () => setTimeout(() => setShowCitySuggestions(false), 150),
                    loading: cityLoading, showSuggestions: showCitySuggestions,
                    suggestions: citySuggestions, onPick: pickEditCitySuggestion, errBag: editErrors,
                    placeholder: 'e.g. Bengaluru, Karnataka',
                  })}
                  {[
                    ['experience_years', 'Experience (years)', 'number'],
                  ].map(([name, label, type]) => (
                    <div key={name}>
                      <label className={LABEL_CLS}>{label}</label>
                      <input
                        type={type}
                        value={editCandidate[name] ?? ''}
                        onChange={(e) => setEditCandidate((c) => ({ ...c, [name]: e.target.value }))}
                        className={INPUT_CLS}
                      />
                      {editErrors[name] && <p className="text-red-500 text-xs mt-1">{editErrors[name][0]}</p>}
                    </div>
                  ))}
                  <div>
                    <label className={LABEL_CLS}>Expected Salary (₹)</label>
                    {salaryPeriodToggle(editCandidate.expected_salary_period ?? 'year', (p) => setEditCandidate((c) => ({ ...c, expected_salary_period: p })))}
                    <input
                      type="number"
                      value={editCandidate.expected_salary ?? ''}
                      onChange={(e) => setEditCandidate((c) => ({ ...c, expected_salary: e.target.value }))}
                      className={INPUT_CLS}
                    />
                    {editErrors.expected_salary && <p className="text-red-500 text-xs mt-1">{editErrors.expected_salary[0]}</p>}
                  </div>
                </div>

                <TagAutocomplete
                  label="Preferred Location"
                  value={editCandidate.preferred_locations ?? []}
                  onChange={(v) => setEditCandidate((c) => ({ ...c, preferred_locations: v }))}
                  options={cityOptions}
                  placeholder="e.g. Pune, Maharashtra"
                  max={5}
                  error={editErrors.preferred_locations}
                />
              </div>

              {/* Section: Professional Details */}
              <div className="mb-5">
                {sectionHeading('💼', 'Professional Details')}
                <div className="grid grid-cols-2 gap-4">
                  {suggestField({
                    name: 'current_company', label: 'Current Company', value: editCandidate.current_company ?? '',
                    onChange: handleEditCompanyChange,
                    onFocus: () => { if (companySuggestions.length > 0) setShowCompanySuggestions(true); },
                    onBlur: () => setTimeout(() => setShowCompanySuggestions(false), 150),
                    loading: companyLoading, showSuggestions: showCompanySuggestions,
                    suggestions: companySuggestions, onPick: pickEditCompanySuggestion, errBag: editErrors,
                  })}

                  {suggestField({
                    name: 'current_designation', label: 'Current Designation', value: editCandidate.current_designation ?? '',
                    onChange: handleEditDesignationChange,
                    onFocus: () => { if (designationSuggestions.length > 0) setShowDesignationSuggestions(true); },
                    onBlur: () => setTimeout(() => setShowDesignationSuggestions(false), 150),
                    loading: designationLoading, showSuggestions: showDesignationSuggestions,
                    suggestions: designationSuggestions, onPick: pickEditDesignationSuggestion, errBag: editErrors,
                  })}
                </div>

                <TagAutocomplete
                  label="Preferred Designation"
                  value={editCandidate.preferred_job_designations ?? []}
                  onChange={(v) => setEditCandidate((c) => ({ ...c, preferred_job_designations: v }))}
                  suggestUrl="/ai/suggest-keywords"
                  placeholder="e.g. Senior Developer"
                  max={10}
                  error={editErrors.preferred_job_designations}
                />

                <div className="mt-4">
                  <label className={LABEL_CLS}>Skills</label>
                  {loadingEditSkills ? (
                    <p className="text-xs text-gray-400">Loading skills…</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {editCandidateSkills.map((s) => (
                        <span key={s.id} className="inline-flex items-center gap-1.5 bg-purple-50 text-purple-700 text-xs font-medium pl-2.5 pr-1.5 py-1 rounded-full">
                          {s.skill_name} <span className="opacity-60 capitalize">({s.proficiency})</span>
                          <button type="button" onClick={() => removeEditCandidateSkill(s.id)} className="hover:text-red-600 leading-none">×</button>
                        </span>
                      ))}
                      {editCandidateSkills.length === 0 && <p className="text-xs text-gray-400">No skills added yet.</p>}
                    </div>
                  )}
                  <div className="space-y-2">
                    <div className="relative">
                      <input
                        type="text"
                        value={editSkillInput}
                        onChange={handleEditSkillInputChange}
                        onFocus={() => { if (editSkillSuggestions.length > 0) setShowEditSkillSuggestions(true); }}
                        onBlur={() => setTimeout(() => setShowEditSkillSuggestions(false), 150)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEditCandidateSkill(); } }}
                        autoComplete="off"
                        placeholder="Skill name (e.g. React)"
                        className={INPUT_CLS + ' w-full pr-7'}
                      />
                      {editSkillLoading && (
                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 border-2 border-gray-300 border-t-purple-600 rounded-full animate-spin" />
                      )}
                      {showEditSkillSuggestions && editSkillSuggestions.length > 0 && (
                        <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto py-1">
                          {editSkillSuggestions.map((s, i) => (
                            <li key={i} onMouseDown={() => addEditCandidateSkill(s)} className="px-3 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600 cursor-pointer transition-colors">
                              {s}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <select
                        value={editSkillProficiency}
                        onChange={(e) => setEditSkillProficiency(e.target.value)}
                        className={INPUT_CLS + ' bg-white flex-1'}
                      >
                        <option value="beginner">Beginner</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced</option>
                        <option value="expert">Expert</option>
                      </select>
                      <button
                        type="button"
                        onClick={() => addEditCandidateSkill()}
                        disabled={!editSkillInput.trim()}
                        className="bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 text-white px-5 py-2 rounded-full text-sm font-semibold disabled:opacity-50 shrink-0 transition-colors"
                      >
                        Add Skill
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section: Resume */}
              <div className="mb-5">
                {sectionHeading('📄', 'Resume')}
                {editCandidate.resume_path && !editResumeFile && (
                  <div className="flex items-center justify-between border border-gray-200 rounded-xl px-4 py-3 mb-2 bg-gray-50">
                    <a
                      href={resumeUrl(editCandidate.resume_path)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-purple-600 font-medium hover:underline flex items-center gap-2"
                    >
                      📄 View current resume
                    </a>
                    <label
                      htmlFor="edit-resume-upload"
                      className="text-xs font-semibold text-gray-500 hover:text-gray-700 cursor-pointer"
                    >
                      Change
                    </label>
                  </div>
                )}
                {editResumeFile ? (
                  <div className="flex items-center justify-between border border-gray-200 rounded-xl px-4 py-3 bg-gray-50">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-xl">📄</span>
                      <span className="text-sm font-medium text-gray-700 truncate">{editResumeFile.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={removeEditResumeSelection}
                      className="text-xs font-semibold text-red-500 hover:text-red-600 shrink-0 ml-3"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  !editCandidate.resume_path && (
                    <label
                      htmlFor="edit-resume-upload"
                      className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl py-6 cursor-pointer hover:border-purple-500 hover:bg-purple-50/40 transition-colors"
                    >
                      <span className="text-2xl mb-1">⬆️</span>
                      <span className="text-sm font-medium text-gray-600">Click to upload resume</span>
                      <span className="text-xs text-gray-400 mt-0.5">PDF, DOC or DOCX — up to 5MB</span>
                    </label>
                  )
                )}
                <input
                  id="edit-resume-upload"
                  ref={editResumeInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleEditResumeChange}
                  className="hidden"
                />
                {editErrors.resume && <p className="text-red-500 text-xs mt-1">{editErrors.resume[0]}</p>}
              </div>

              {/* Section: Source & Notes */}
              <div>
                {sectionHeading('🗂️', 'Source & Notes')}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={LABEL_CLS}>Source</label>
                    <select
                      value={editCandidate.source ?? 'other'}
                      onChange={(e) => setEditCandidate((c) => ({ ...c, source: e.target.value }))}
                      className={INPUT_CLS + ' bg-white'}
                    >
                      <option value="referral">Referral</option>
                      <option value="naukri">Naukri</option>
                      <option value="linkedin">LinkedIn</option>
                      <option value="walk_in">Walk-in</option>
                      <option value="other">Other</option>
                    </select>
                    {editErrors.source && <p className="text-red-500 text-xs mt-1">{editErrors.source[0]}</p>}
                  </div>
                  <div>
                    <label className={LABEL_CLS}>Stage</label>
                    <select
                      value={editCandidate.stage ?? 'new'}
                      onChange={(e) => setEditCandidate((c) => ({ ...c, stage: e.target.value }))}
                      className={INPUT_CLS + ' bg-white capitalize'}
                    >
                      {['new', 'screening', 'interview', 'offered', 'hired', 'rejected'].map((s) => (
                        <option key={s} value={s} className="capitalize">{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-4">
                  <label className={LABEL_CLS}>Notes</label>
                  <textarea
                    value={editCandidate.notes ?? ''}
                    onChange={(e) => setEditCandidate((c) => ({ ...c, notes: e.target.value }))}
                    className={INPUT_CLS + ' resize-none'}
                    rows={2}
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 shrink-0">
              <button
                type="button"
                onClick={openConvert}
                className="mr-auto text-sm font-medium text-amber-700 hover:text-amber-800 px-3 py-2 rounded-lg hover:bg-amber-50 transition-colors"
              >
                🔁 Convert to Employer
              </button>
              <button
                type="button"
                onClick={closeEditModal}
                className="text-sm font-medium text-gray-500 hover:text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={savingEdit}
                className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors shadow-sm"
              >
                {savingEdit ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        </div>
      )}
      {convertOpen && editCandidate && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
          <form onSubmit={handleConvert} className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Convert to Employer</h3>
              <p className="text-xs text-gray-500 mt-1">
                {editCandidate.name} ({editCandidate.phone}) will move from Candidates to Employers. Phone and password stay the same.
              </p>
            </div>
            <div className="px-6 py-4 space-y-3">
              {convertErrors.general && <p className="text-red-500 text-sm">{convertErrors.general[0]}</p>}
              <div>
                <label className={LABEL_CLS}>Company name *</label>
                <input className={INPUT_CLS} value={convertForm.company_name}
                  onChange={(e) => setConvertForm((f) => ({ ...f, company_name: e.target.value }))} required />
                {convertErrors.company_name && <p className="text-red-500 text-xs mt-1">{convertErrors.company_name[0]}</p>}
              </div>
              <div>
                <label className={LABEL_CLS}>Email *</label>
                <input type="email" className={INPUT_CLS} value={convertForm.email}
                  onChange={(e) => setConvertForm((f) => ({ ...f, email: e.target.value }))} required />
                {convertErrors.email && <p className="text-red-500 text-xs mt-1">{convertErrors.email[0]}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={LABEL_CLS}>City</label>
                  <input className={INPUT_CLS} value={convertForm.city}
                    onChange={(e) => setConvertForm((f) => ({ ...f, city: e.target.value }))} />
                </div>
                <div>
                  <label className={LABEL_CLS}>Industry</label>
                  <input className={INPUT_CLS} value={convertForm.company_industry}
                    onChange={(e) => setConvertForm((f) => ({ ...f, company_industry: e.target.value }))} />
                </div>
                <div>
                  <label className={LABEL_CLS}>Website</label>
                  <input className={INPUT_CLS} value={convertForm.company_website}
                    onChange={(e) => setConvertForm((f) => ({ ...f, company_website: e.target.value }))} />
                </div>
                <div>
                  <label className={LABEL_CLS}>GST</label>
                  <input className={INPUT_CLS} value={convertForm.company_gst}
                    onChange={(e) => setConvertForm((f) => ({ ...f, company_gst: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className={LABEL_CLS}>Call note (what they said on the call)</label>
                <textarea rows={2} className={INPUT_CLS + ' resize-none'} value={convertForm.call_note}
                  onChange={(e) => setConvertForm((f) => ({ ...f, call_note: e.target.value }))} />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
              <button type="button" onClick={() => setConvertOpen(false)}
                className="text-sm font-medium text-gray-500 hover:text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-100">
                Cancel
              </button>
              <button type="submit" disabled={converting}
                className="bg-amber-600 hover:bg-amber-700 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
                {converting ? 'Converting…' : 'Convert to Employer'}
              </button>
            </div>
          </form>
        </div>
      )}

      {fixOpen && (
  <FixLocationsModal
    candidates={candidates}
    onClose={() => setFixOpen(false)}
    onApplied={handleLocationApplied}
  />
)}
      </div>
    </AgentLayout>
  );
}

export default AgentCandidates;